import { Button, Drawer, HStack, Icon, Portal, Spinner, Text } from "@chakra-ui/react";
import { IoCloseSharp } from "react-icons/io5";
import { useEffect, useState } from "react";
import ClientService from "@/services/ClientService";
import { showToast } from "@/components/ui/toaster";
import CompanyOwnerFormFields from "./CompanyOwnerFormFields";
import { companyOwnerFormFrom, emptyCompanyOwnerForm, validateCompanyOwnerForm } from "../companyOwnerForm";
import { companyLabel } from "../companyLabel";

/** One form for the company record and its owner account (spec: never two separate forms). */
const EditCompanyDrawer = ({ isOpen, company, onClose, onSaved }) => {
    const [form, setForm] = useState(emptyCompanyOwnerForm);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) setForm(companyOwnerFormFrom(company?.owner));
    }, [isOpen, company]);

    const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSave = async () => {
        const error = validateCompanyOwnerForm(form);
        if (error) {
            showToast("Error", error, "error");
            return;
        }
        setIsSaving(true);
        try {
            await ClientService.updateCompany(company.id, { ...form, isActive: company.status === "Active" });
            showToast("Company updated", null, "success");
            onSaved();
            onClose();
        } catch (err) {
            showToast("Error", err?.response?.data ?? "Failed to update company", "error");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && !isSaving && onClose()} size="lg" placement="end">
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner padding="4">
                    <Drawer.Content bg="bg.drawer" borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent="space-between" w="full">
                                <Text color="white">Edit client — {companyLabel(company)}</Text>
                                <Icon fontSize={32} color="text.grey" onClick={onClose} cursor="pointer"><IoCloseSharp /></Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <CompanyOwnerFormFields form={form} set={set} />
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent="flex-end" w="full" gap={3}>
                                <Button variant="outlined" onClick={onClose} disabled={isSaving}>Cancel</Button>
                                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? <Spinner size="sm" /> : "Save"}
                                </Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};

export default EditCompanyDrawer;
